import { useMemo, useCallback, useState, useEffect, type MouseEvent } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Loader2, Plus, RefreshCw, Target } from 'lucide-react';
import { CampaignTypePickerModal } from '@/components/campaign-create/CampaignTypePickerModal';
import { EditableKeywordBidCell } from '@/components/EditableKeywordBidCell';
import { Badge } from '@/components/ui/Badge';
import { useWorkspaceCatalog } from '@/hooks/useWorkspaceCatalog';
import {
  useWorkspaceCoreReportMetrics,
  useWorkspaceDeferredReportMetrics,
} from '@/hooks/useWorkspaceReportMetrics';
import { useWorkspaceRetailKpis } from '@/hooks/useWorkspaceRetailKpis';
import {
  applyColumnFilters,
  buildEntityGraph,
  buildWorkspaceResolutionContext,
  isCrossFilterEmpty,
  normalizeCrossFilter,
  resolveWorkspaceConstraints,
  toggleAdGroupFilter,
  toggleDimensionIds,
  toggleKeywordFilter,
  type EntityCrossFilterDimension,
  type EntityGraph,
} from '@/utils/entityCrossFilter';
import {
  buildScopedCampaignLookupKeys,
  metricsForAdGroup,
  totalsFromAdProductMaps,
  totalsFromMetricRows,
  WORKSPACE_METRIC_COLUMNS,
} from '@/utils/workspaceAdProductMetrics';
import { previousMetricsForKeywordRow } from '@/utils/workspaceKeywordCompare';
import { useAppDateRange } from '@/contexts/AppDateRangeContext';
import { WorkspaceFilterBar } from '@/components/workspace/WorkspaceFilterBar';
import { useToast } from '@/components/ui/Toast';
import { useAuth } from '@/contexts/AuthContext';
import { enqueueProfileReportSync } from '@/api/accountAdminSync';
import {
  WorkspaceTableSelectionActions,
  WORKSPACE_FILTER_DIMENSIONS,
} from '@/components/workspace/WorkspaceTableSelectionActions';
import type { WorkspaceDimensionSelectionProps } from '@/components/workspace/workspaceSelectionTypes';
import { useWorkspaceDemoActions } from '@/components/workspace/useWorkspaceDemoActions';
import {
  FilterSearch,
  FilterSelect,
  SelectionCheckbox,
  WorkspaceCheckboxCell,
  WorkspaceMiddleCell,
  tableRowClass,
  ToolbarRow,
  WorkspaceSection,
  WorkspaceTablePagination,
  WorkspacePageSkeleton,
  WorkspaceTableShell,
  workspaceTableClass,
} from '@/components/workspace/WorkspaceSection';
import {
  WorkspaceOrderedHeaders,
  WorkspaceOrderedMetricCells,
  WorkspaceOrderedTotalsRow,
} from '@/components/workspace/WorkspaceOrderedHeaders';
import { ReportColumnPicker } from '@/components/ReportColumnPicker';
import { WorkspaceExportButton } from '@/components/workspace/WorkspaceExportButton';
import { WorkspaceMetricFiltersPopover } from '@/components/workspace/WorkspaceMetricFiltersPopover';
import { useMetricFilters } from '@/components/workspace/useMetricFilters';
import {
  exportWorkspaceTableCsv,
  fetchAllWorkspaceEntityRows,
  workspaceExportColumns,
} from '@/api/workspaceCsvExport';
import { useColumnOrder } from '@/components/workspace/useColumnOrder';
import { useWorkspaceFrozenLayout } from '@/components/workspace/workspaceStickyColumns';
import { useTableSort } from '@/components/workspace/useTableSort';
import {
  AD_GROUP_COLUMN_IDS,
  AD_GROUP_ENTITY_COLUMNS,
  KEYWORD_COLUMN_IDS,
  KEYWORD_ENTITY_COLUMNS,
  PORTFOLIO_ENTITY_COLUMNS,
  defaultColumnIds,
  entityColumnsForDemo,
  workspaceColumnPickerOptions,
} from '@/components/workspace/workspaceColumnConfig';
import {
  WorkspaceAdProductMetricsTable,
  formatWorkspaceRangeSubtitle,
} from '@/components/workspace/WorkspaceAdProductMetricsTable';
import {
  DEFAULT_VISIBLE_WORKSPACE_KPI_KEYS,
  WorkspaceKpiSummary,
  type WorkspaceKpiKey,
} from '@/components/workspace/WorkspaceKpiSummary';
import { WorkspaceCustomizeMetricsPopover } from '@/components/workspace/WorkspaceCustomizeMetricsPopover';
import { WorkspaceTargetingTypeMetricsTable } from '@/components/workspace/WorkspaceTargetingTypeMetricsTable';
import { WorkspaceMatchTypeMetricsTable } from '@/components/workspace/WorkspaceMatchTypeMetricsTable';
import { WorkspacePlacementMetricsTable } from '@/components/workspace/WorkspacePlacementMetricsTable';
import { WorkspaceAudienceMetricsTable } from '@/components/workspace/WorkspaceAudienceMetricsTable';
import { WorkspaceSearchTermsTable } from '@/components/workspace/WorkspaceSearchTermsTable';
import { WorkspaceAdvertisedProductTable } from '@/components/workspace/WorkspaceAdvertisedProductTable';
import { WorkspaceAdProductsTable } from '@/components/workspace/WorkspaceAdProductsTable';
import { useWorkspaceProductAds } from '@/hooks/useWorkspaceProductAds';
import { useServerPagination } from '@/hooks/useServerPagination';
import {
  useWorkspaceAdGroupsPage,
  useWorkspaceKeywordsPage,
} from '@/hooks/useWorkspaceEntityPages';
import { useWorkspaceState } from '@/components/workspace/useWorkspaceState';
import { WorkspaceCampaignsSection } from '@/components/workspace/WorkspaceCampaignsSection';
import { WorkspacePortfoliosSection } from '@/components/workspace/WorkspacePortfoliosSection';
import { WORKSPACE_CAMPAIGNS_SECTION_ID } from '@/components/workspace/workspacePaths';
import { workspaceKeywordMatchLabel } from '@/utils/workspaceMatchTypeMetrics';
import { formatCurrencyValue } from '@/api/keywordMetrics';
import { LazyMount } from '@/components/ui/LazyMount';
import {
  resolveEditableWorkspaceKeywordBid,
} from '@/api/keywordBidUpdate';
import {
  adGroupLevelActionOptions,
  applyWorkspaceAdGroupStateChange,
  keywordLevelActionOptions,
  summarizeWorkspaceAdGroupStateChange,
} from '@/api/workspaceBulkActions';
import { AD_GROUP_STATUS_UPDATE_FALLBACK_MESSAGE } from '@/api/adGroupStateUpdate';
import {
  WorkspaceKeywordBidModal,
  WorkspaceKeywordBidProgressModal,
  WorkspaceStateChangeModal,
  WorkspaceSuggestedBidModal,
} from '@/components/workspace/WorkspaceBulkActionModals';
import { useWorkspaceKeywordBidJob } from '@/hooks/useWorkspaceKeywordBidJob';
import { useWorkspaceKeywordSuggestedBids } from '@/hooks/useWorkspaceKeywordSuggestedBids';
import type { WorkspaceKeywordBidAdjustMode } from '@/api/workspaceKeywordBidTypes';

export function WorkspacePage() {
  const { profileId: profileIdParam } = useParams<{ profileId: string }>();
  const profileId = profileIdParam ? decodeURIComponent(profileIdParam) : '';
  const navigate = useNavigate();
  const location = useLocation();
  const { showToast } = useToast();
  const { user } = useAuth();
  const isDemo = Boolean(user?.is_demo);
  const queryClient = useQueryClient();
  const workspaceCampaignsHash = `#${WORKSPACE_CAMPAIGNS_SECTION_ID}`;

  const openCampaignDetails = (event: MouseEvent<HTMLAnchorElement>, to: string) => {
    if (location.hash === workspaceCampaignsHash) return;
    event.preventDefault();
    navigate(
      {
        pathname: location.pathname,
        search: location.search,
        hash: workspaceCampaignsHash,
      },
      { replace: true }
    );
    navigate(to);
  };

  const { range: rangeState } = useAppDateRange();

  const [deferredSectionsVisible, setDeferredSectionsVisible] = useState(false);
  const [productAdsVisible, setProductAdsVisible] = useState(false);
  const [adGroupStatusModalOpen, setAdGroupStatusModalOpen] = useState(false);
  const [adGroupStatusApplying, setAdGroupStatusApplying] = useState(false);
  const [keywordBidModalOpen, setKeywordBidModalOpen] = useState(false);
  const [keywordSuggestedBidModalOpen, setKeywordSuggestedBidModalOpen] = useState(false);
  const [keywordSelectAllMatching, setKeywordSelectAllMatching] = useState(false);
  const [createCampaignPickerOpen, setCreateCampaignPickerOpen] = useState(false);
  const keywordBidJob = useWorkspaceKeywordBidJob(profileId);
  const catalog = useWorkspaceCatalog(profileId);
  const {
    appliedFilter,
    pendingFilter,
    setPendingFilter,
    columnFilters,
    updateColumnFilters,
    applyDimensionFilter,
    discardDimensionSelection,
    dimensionSelectionMeta,
    clearAllFilters,
    clearDimension,
    removeFromApplied,
    removeStaleFromApplied,
    hasActiveCrossFilter,
    filterEntities,
  } = useWorkspaceState(profileId);

  const normalizedPending = normalizeCrossFilter(pendingFilter);
  const normalizedApplied = normalizeCrossFilter(appliedFilter);
  const placementPinActive = normalizedApplied.placements.length > 0;
  const productPinActive =
    normalizedApplied.asins.length > 0 || normalizedApplied.adProductRowKeys.length > 0;

  const portfolioSelection = dimensionSelectionMeta(WORKSPACE_FILTER_DIMENSIONS.portfolios);
  const campaignSelection = dimensionSelectionMeta(WORKSPACE_FILTER_DIMENSIONS.campaigns);
  const adGroupSelection = dimensionSelectionMeta(WORKSPACE_FILTER_DIMENSIONS.adGroups);
  const keywordSelection = dimensionSelectionMeta(WORKSPACE_FILTER_DIMENSIONS.keywords);

  const graph: EntityGraph | null = useMemo(() => {
    if (!profileId || catalog.isLoading) return null;
    return buildEntityGraph({
      portfolios: catalog.portfolios,
      campaigns: catalog.campaigns,
      adGroups: catalog.adGroups,
      spKeywords: catalog.spKeywords,
      sbKeywords: catalog.sbKeywords,
    });
  }, [
    profileId,
    catalog.isLoading,
    catalog.portfolios,
    catalog.campaigns,
    catalog.adGroups,
    catalog.spKeywords,
    catalog.sbKeywords,
  ]);

  const columnFiltered = useMemo(
    () => (graph ? applyColumnFilters(graph, columnFilters) : null),
    [graph, columnFilters]
  );

  /**
   * Metric hooks need a campaign scope, but full resolution needs report/product rows from those
   * hooks. Keep a synced scope state updated after each resolution pass (one-frame lag is fine;
   * raw keyword/target rows are unscoped so attribute maps stay correct).
   */
  const [syncedScope, setSyncedScope] = useState<Set<string> | null | undefined>(undefined);
  const [visibleKpiKeys, setVisibleKpiKeys] = useState<WorkspaceKpiKey[]>(
    () => [...DEFAULT_VISIBLE_WORKSPACE_KPI_KEYS]
  );

  const reportMetrics = useWorkspaceCoreReportMetrics(
    profileId,
    rangeState,
    graph,
    syncedScope
  );

  const retailKpis = useWorkspaceRetailKpis(profileId, rangeState);

  const deferredMetrics = useWorkspaceDeferredReportMetrics(
    profileId,
    rangeState,
    graph,
    syncedScope,
    reportMetrics.coreSettled,
    deferredSectionsVisible || placementPinActive
  );

  const campaignNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const campaign of catalog.campaigns) {
      map.set(campaign.campaignId, campaign.name);
    }
    return map;
  }, [catalog.campaigns]);

  const demoCampaignOptions = useMemo(
    () => catalog.campaigns.map((c) => ({ id: c.campaignId, name: c.name })),
    [catalog.campaigns]
  );

  const demoAdGroupOptions = useMemo(
    () =>
      catalog.adGroups.map((ag) => ({
        id: ag.adGroupId,
        name: ag.name,
        campaignId: ag.campaignId,
      })),
    [catalog.adGroups]
  );

  const demoPortfolioOptions = useMemo(
    () => catalog.portfolios.map((p) => ({ id: p.portfolioId, name: p.name })),
    [catalog.portfolios]
  );

  const { openDemoAction, demoModals } = useWorkspaceDemoActions({
    showToast,
    campaigns: demoCampaignOptions,
    adGroups: demoAdGroupOptions,
    portfolios: demoPortfolioOptions,
  });

  const productAds = useWorkspaceProductAds(
    profileId,
    campaignNameById,
    syncedScope,
    reportMetrics.coreSettled && (productAdsVisible || productPinActive)
  );

  const resolutionCtx = useMemo(() => {
    if (!graph) return {};
    return buildWorkspaceResolutionContext({
      graph,
      keywordRows: reportMetrics.keywordRows,
      targetRows: reportMetrics.targetRows,
      placementByCampaign: deferredMetrics.placementMetricsByCampaign,
      productAdCatalog: productAds.catalog,
      adProductRows: productAds.adProducts,
    });
  }, [
    graph,
    reportMetrics.keywordRows,
    reportMetrics.targetRows,
    deferredMetrics.placementMetricsByCampaign,
    productAds.catalog,
    productAds.adProducts,
  ]);

  const visible = useMemo(
    () => (graph ? filterEntities(graph, appliedFilter, columnFilters, resolutionCtx) : null),
    [graph, appliedFilter, columnFilters, filterEntities, resolutionCtx]
  );

  const scopedCampaignLookupKeys = useMemo(() => {
    if (!graph) return undefined;
    if (isCrossFilterEmpty(appliedFilter)) return null;
    const constraints = resolveWorkspaceConstraints(graph, appliedFilter, resolutionCtx);
    if (constraints.conflict) return new Set<string>();
    const campaigns = graph.campaigns.filter((c) => constraints.campaignKeys.has(c.campaignKey));
    return buildScopedCampaignLookupKeys(campaigns);
  }, [graph, appliedFilter, resolutionCtx]);

  // Sync hook scope after resolution (compare by content to avoid loops on new Set refs).
  useEffect(() => {
    setSyncedScope((prev) => {
      if (scopedCampaignLookupKeys === undefined) {
        return prev === undefined ? prev : undefined;
      }
      if (scopedCampaignLookupKeys === null) {
        return prev === null ? prev : null;
      }
      if (
        prev instanceof Set &&
        prev.size === scopedCampaignLookupKeys.size &&
        [...scopedCampaignLookupKeys].every((k) => prev.has(k))
      ) {
        return prev;
      }
      return scopedCampaignLookupKeys;
    });
  }, [scopedCampaignLookupKeys]);

  const rangeSubtitle = formatWorkspaceRangeSubtitle(
    rangeState.current.startDate,
    rangeState.current.endDate,
    rangeState.previous.startDate,
    rangeState.previous.endDate,
    rangeState.compareEnabled
  );

  const compareEnabled = rangeState.compareEnabled;
  const coreLoading = reportMetrics.coreLoading;
  const adProductMetricsLoading = reportMetrics.adProductLoading;
  const deferredLoading = deferredMetrics.deferredLoading;
  const metricsError = reportMetrics.error ?? deferredMetrics.error;

  const kpiCurrentTotals = useMemo(
    () => totalsFromAdProductMaps(reportMetrics.current),
    [reportMetrics.current]
  );
  const kpiPreviousTotals = useMemo(
    () => totalsFromAdProductMaps(reportMetrics.previous),
    [reportMetrics.previous]
  );

  const adGroupSort = useTableSort({ defaultSortBy: 'name' });
  const keywordSort = useTableSort({ defaultSortBy: 'keywordText' });
  const adGroupMetricFilters = useMetricFilters({
    tableId: 'ad-groups',
    metrics: WORKSPACE_METRIC_COLUMNS,
  });
  const keywordMetricFilters = useMetricFilters({
    tableId: 'keywords',
    metrics: WORKSPACE_METRIC_COLUMNS,
  });
  const adGroupEntityColumns = useMemo(
    () => entityColumnsForDemo(AD_GROUP_ENTITY_COLUMNS, isDemo),
    [isDemo]
  );
  const adGroupColumnIds = useMemo(
    () => (isDemo ? defaultColumnIds(adGroupEntityColumns) : AD_GROUP_COLUMN_IDS),
    [isDemo, adGroupEntityColumns]
  );

  const adGroupColumns = useColumnOrder(adGroupColumnIds);
  const keywordColumns = useColumnOrder(KEYWORD_COLUMN_IDS);
  const adGroupFreeze = useWorkspaceFrozenLayout(
    adGroupColumns.frozenColumnIds,
    adGroupColumns.displayOrder.join('\0')
  );
  const keywordFreeze = useWorkspaceFrozenLayout(
    keywordColumns.frozenColumnIds,
    keywordColumns.displayOrder.join('\0')
  );

  const adGroupPageResetKey = [
    columnFilters.adGroupSearch,
    columnFilters.adGroupState,
    columnFilters.adGroupCampaignState,
    columnFilters.adGroupAdProduct,
    JSON.stringify(normalizedApplied),
    rangeState.current.startDate,
    rangeState.current.endDate,
    adGroupSort.sortBy,
    adGroupSort.sortDir,
    adGroupMetricFilters.serialized,
  ].join('|');
  const keywordPageResetKey = [
    columnFilters.keywordSearch,
    columnFilters.keywordState,
    columnFilters.keywordMatchType,
    JSON.stringify(normalizedApplied),
    rangeState.current.startDate,
    rangeState.current.endDate,
    keywordSort.sortBy,
    keywordSort.sortDir,
    keywordMetricFilters.serialized,
  ].join('|');

  const adGroupPager = useServerPagination(adGroupPageResetKey);
  const keywordPager = useServerPagination(keywordPageResetKey);

  // Server entity pages reload their own catalog graph — do not wait for the FE catalog waterfall.
  const entityPagesEnabled = Boolean(profileId);

  const adGroupsPage = useWorkspaceAdGroupsPage({
    profileId,
    startDate: rangeState.current.startDate,
    endDate: rangeState.current.endDate,
    page: adGroupPager.page,
    pageSize: adGroupPager.pageSize,
    search: columnFilters.adGroupSearch,
    status: columnFilters.adGroupState,
    adProduct: columnFilters.adGroupAdProduct,
    campaignStatus: columnFilters.adGroupCampaignState,
    appliedFilter: normalizedApplied,
    sortBy: adGroupSort.sortBy,
    sortDir: adGroupSort.sortDir,
    metricFilters: adGroupMetricFilters.payload,
    enabled: entityPagesEnabled,
  });
  const keywordsPage = useWorkspaceKeywordsPage({
    profileId,
    startDate: rangeState.current.startDate,
    endDate: rangeState.current.endDate,
    page: keywordPager.page,
    pageSize: keywordPager.pageSize,
    search: columnFilters.keywordSearch,
    status: columnFilters.keywordState,
    matchType: columnFilters.keywordMatchType,
    appliedFilter: normalizedApplied,
    sortBy: keywordSort.sortBy,
    sortDir: keywordSort.sortDir,
    metricFilters: keywordMetricFilters.payload,
    enabled: entityPagesEnabled,
  });
  const handleKeywordBidUpdated = () => {
    void queryClient.invalidateQueries({
      queryKey: ['workspace-entity-page', 'keywords', profileId],
    });
    void queryClient.invalidateQueries({ queryKey: ['sp-keywords'] });
    void queryClient.invalidateQueries({ queryKey: ['sb-keywords'] });
    catalog.refetchAll();
  };
  const keywordSuggestedBids = useWorkspaceKeywordSuggestedBids({
    profileId,
    rows: keywordsPage.rows,
    enabled: keywordColumns.visibleIds.includes('bid'),
  });

  useEffect(() => {
    setKeywordSelectAllMatching(false);
  }, [
    columnFilters.keywordSearch,
    columnFilters.keywordState,
    columnFilters.keywordMatchType,
    normalizedApplied,
    keywordsPage.totalCount,
  ]);

  const makeDimensionSelection = useCallback(
    (dimension: EntityCrossFilterDimension): WorkspaceDimensionSelectionProps => {
      const meta = dimensionSelectionMeta(dimension);
      return {
        pendingIds: normalizedPending[dimension],
        selectionCount: meta.selectionCount,
        hasPendingChanges: meta.hasPendingChanges,
        canApply: meta.canApply,
        onToggleId: (id, selected) =>
          setPendingFilter((prev) => toggleDimensionIds(prev, dimension, [id], selected)),
        onToggleAll: (ids, selected) =>
          setPendingFilter((prev) => toggleDimensionIds(prev, dimension, ids, selected)),
        onApply: () => applyDimensionFilter(dimension),
        onClearSelection: () => discardDimensionSelection(dimension),
      };
    },
    [
      dimensionSelectionMeta,
      normalizedPending,
      setPendingFilter,
      applyDimensionFilter,
      discardDimensionSelection,
    ]
  );

  const handleRemoveChip = useCallback(
    (dimension: keyof typeof appliedFilter, id: string) => {
      removeFromApplied(dimension, id);
    },
    [removeFromApplied]
  );

  const handleRemoveStale = useCallback(() => {
    if (!graph) return;
    removeStaleFromApplied(graph);
  }, [graph, removeStaleFromApplied]);

  const enableDeferredSections = useCallback(() => setDeferredSectionsVisible(true), []);
  const enableProductAdsSections = useCallback(() => setProductAdsVisible(true), []);

  const handleRefresh = useCallback(() => {
    catalog.refetchAll();
    reportMetrics.refetch();
    deferredMetrics.refetch();
    productAds.refetch();
  }, [catalog, reportMetrics, deferredMetrics, productAds]);

  const reportSyncMutation = useMutation({
    mutationFn: async () => {
      if (!profileId) throw new Error('Select a profile to sync reports');
      await enqueueProfileReportSync(profileId);
    },
    onSuccess: () => {
      showToast(
        'Report sync queued. Remaining windows resume automatically if Amazon rate-limits.',
        'success'
      );
    },
    onError: (err) => {
      showToast((err as Error).message || 'Report sync failed to start', 'error');
    },
  });

  const budgetByCampaignKey = useMemo(() => {
    const map = new Map<string, number | null>();
    if (!graph) return map;
    for (const campaign of graph.campaigns) {
      map.set(
        campaign.campaignKey,
        typeof campaign.raw.budget === 'number' ? campaign.raw.budget : null
      );
    }
    return map;
  }, [graph]);

  const exportEntityTable = useCallback(
    async (args: {
      entity: 'portfolios' | 'campaigns' | 'ad-groups' | 'keywords';
      filenamePrefix: string;
      entityColumns: typeof PORTFOLIO_ENTITY_COLUMNS;
      visibleIds: string[];
      columnFilters?: {
        search?: string;
        status?: string;
        adProduct?: string;
        matchType?: string;
        campaignStatus?: string;
      };
      sortBy?: string;
      sortDir?: 'asc' | 'desc';
      metricFilters?: typeof adGroupMetricFilters.payload;
    }) => {
      try {
        const fetched = await fetchAllWorkspaceEntityRows({
          profileId,
          entity: args.entity,
          startDate: rangeState.current.startDate,
          endDate: rangeState.current.endDate,
          columnFilters: args.columnFilters,
          appliedFilter: normalizedApplied as unknown as Record<string, unknown>,
          sortBy: args.sortBy,
          sortDir: args.sortDir,
          metricFilters: args.metricFilters,
        });
        const columns = workspaceExportColumns(args.visibleIds, args.entityColumns);
        exportWorkspaceTableCsv({
          filenamePrefix: args.filenamePrefix,
          startDate: rangeState.current.startDate,
          endDate: rangeState.current.endDate,
          columns,
          rows: fetched.rows,
          extras: {
            campaignNameById,
            budgetByCampaignKey,
          },
        });
        if (fetched.truncated) {
          showToast(
            `Exported first ${fetched.rows.length.toLocaleString()} of ${fetched.totalCount.toLocaleString()} rows`,
            'error'
          );
        } else {
          showToast(`Exported ${fetched.rows.length.toLocaleString()} rows`, 'success');
        }
      } catch (error) {
        showToast(
          error instanceof Error ? error.message : 'Failed to export table',
          'error'
        );
      }
    },
    [
      profileId,
      rangeState.current.startDate,
      rangeState.current.endDate,
      normalizedApplied,
      campaignNameById,
      budgetByCampaignKey,
      showToast,
    ]
  );

  const selectedAdGroups = useMemo(() => {
    const pageById = new Map(adGroupsPage.rows.map((row) => [row.adGroupId, row]));
    return pendingFilter.adGroupIds
      .map((id) => {
        const fromGraph = graph?.indices.adGroupsById.get(id);
        if (fromGraph) {
          return {
            adGroupId: fromGraph.adGroupId,
            adProduct: fromGraph.adProduct,
            name: fromGraph.name,
          };
        }
        const fromPage = pageById.get(id);
        if (fromPage) {
          return {
            adGroupId: fromPage.adGroupId,
            adProduct: fromPage.adProduct,
            name: fromPage.name,
          };
        }
        return null;
      })
      .filter((row): row is NonNullable<typeof row> => row != null);
  }, [adGroupsPage.rows, graph, pendingFilter.adGroupIds]);

  const keywordActionSelectionCount = keywordSelectAllMatching
    ? keywordsPage.totalCount
    : keywordSelection.selectionCount;

  const handleKeywordActionSelect = useCallback(
    (actionId: string) => {
      if (actionId === 'change_bid') {
        if (keywordActionSelectionCount <= 0) {
          showToast('Select at least one keyword', 'error');
          return;
        }
        if (isDemo) {
          openDemoAction(actionId, {
            entityLabel: 'keyword',
            selectionCount: keywordActionSelectionCount,
          });
          return;
        }
        setKeywordBidModalOpen(true);
        return;
      }
      if (actionId === 'set_suggested_bid') {
        if (keywordActionSelectionCount <= 0) {
          showToast('Select at least one keyword', 'error');
          return;
        }
        setKeywordSuggestedBidModalOpen(true);
        return;
      }
      if (isDemo) {
        openDemoAction(actionId, {
          entityLabel: 'keyword',
          selectionCount: keywordActionSelectionCount,
        });
      }
    },
    [
      isDemo,
      openDemoAction,
      keywordActionSelectionCount,
      showToast,
    ]
  );

  const keywordBidSelectionBody = useCallback(() => {
    if (keywordSelectAllMatching) {
      return {
        selectAllMatching: true as const,
        startDate: rangeState.current.startDate,
        endDate: rangeState.current.endDate,
        appliedFilter: normalizedApplied as unknown as Record<string, unknown>,
        columnFilters: {
          search: columnFilters.keywordSearch || undefined,
          status:
            columnFilters.keywordState && columnFilters.keywordState !== 'ALL'
              ? columnFilters.keywordState
              : undefined,
          matchType:
            columnFilters.keywordMatchType && columnFilters.keywordMatchType !== 'ALL'
              ? columnFilters.keywordMatchType
              : undefined,
        },
      };
    }
    return { keywordIds: [...normalizedPending.keywordIds] };
  }, [
    keywordSelectAllMatching,
    rangeState.current.startDate,
    rangeState.current.endDate,
    normalizedApplied,
    columnFilters.keywordSearch,
    columnFilters.keywordState,
    columnFilters.keywordMatchType,
    normalizedPending.keywordIds,
  ]);

  const handleConfirmKeywordBid = useCallback(
    async (mode: WorkspaceKeywordBidAdjustMode, value: number) => {
      if (!Number.isFinite(value) || value <= 0) {
        showToast('Enter a valid adjustment value greater than 0', 'error');
        return;
      }
      if (mode === 'DECREASE_PERCENT' && value > 100) {
        showToast('Decrease percentage cannot exceed 100%', 'error');
        return;
      }
      if (keywordActionSelectionCount <= 0) {
        showToast('Select at least one keyword', 'error');
        return;
      }

      setKeywordBidModalOpen(false);
      try {
        const body = {
          mode,
          value,
          ...keywordBidSelectionBody(),
        };
        const final = await keywordBidJob.start(body);
        const failed = final.totals.failed ?? 0;
        const applied = final.totals.applied ?? 0;
        if (final.status === 'failed') {
          showToast(final.applyProgress.error || 'Bid change job failed', 'error');
        } else if (failed > 0) {
          showToast(`Updated ${applied} keyword${applied === 1 ? '' : 's'}, ${failed} failed`, 'error');
        } else {
          showToast(`Updated bid for ${applied} keyword${applied === 1 ? '' : 's'}`, 'success');
          setKeywordSelectAllMatching(false);
          discardDimensionSelection(WORKSPACE_FILTER_DIMENSIONS.keywords);
        }
      } catch (error) {
        const detail =
          error &&
          typeof error === 'object' &&
          'response' in error &&
          (error as { response?: { data?: { detail?: unknown } } }).response?.data?.detail;
        showToast(
          typeof detail === 'string'
            ? detail
            : error instanceof Error
              ? error.message
              : 'Failed to apply keyword bid changes',
          'error'
        );
      }
    },
    [
      keywordActionSelectionCount,
      keywordBidSelectionBody,
      keywordBidJob,
      discardDimensionSelection,
      showToast,
    ]
  );

  const handleConfirmSuggestedBid = useCallback(async () => {
    if (keywordActionSelectionCount <= 0) {
      showToast('Select at least one keyword', 'error');
      return;
    }

    setKeywordSuggestedBidModalOpen(false);
    try {
      const final = await keywordBidJob.start({
        mode: 'SET_SUGGESTED',
        ...keywordBidSelectionBody(),
      });
      const failed = final.totals.failed ?? 0;
      const applied = final.totals.applied ?? 0;
      if (final.status === 'failed') {
        showToast(final.applyProgress.error || 'Bid change job failed', 'error');
      } else if (failed > 0) {
        showToast(`Updated ${applied} keyword${applied === 1 ? '' : 's'}, ${failed} failed`, 'error');
      } else {
        showToast(`Updated bid for ${applied} keyword${applied === 1 ? '' : 's'}`, 'success');
        setKeywordSelectAllMatching(false);
        discardDimensionSelection(WORKSPACE_FILTER_DIMENSIONS.keywords);
      }
    } catch (error) {
      const detail =
        error &&
        typeof error === 'object' &&
        'response' in error &&
        (error as { response?: { data?: { detail?: unknown } } }).response?.data?.detail;
      showToast(
        typeof detail === 'string'
          ? detail
          : error instanceof Error
            ? error.message
            : 'Failed to apply keyword bid changes',
        'error'
      );
    }
  }, [
    keywordActionSelectionCount,
    keywordBidSelectionBody,
    keywordBidJob,
    discardDimensionSelection,
    showToast,
  ]);

  const handleAdGroupActionSelect = useCallback(
    (actionId: string) => {
      if (actionId === 'change_status') {
        if (adGroupSelection.selectionCount <= 0) {
          showToast('Select at least one ad group', 'error');
          return;
        }
        setAdGroupStatusModalOpen(true);
      }
    },
    [adGroupSelection.selectionCount, showToast]
  );

  const handleConfirmAdGroupState = useCallback(
    async (state: 'ENABLED' | 'PAUSED') => {
      if (!selectedAdGroups.length) {
        showToast('Select at least one ad group', 'error');
        return;
      }
      setAdGroupStatusApplying(true);
      try {
        const result = await applyWorkspaceAdGroupStateChange(profileId, selectedAdGroups, state);
        showToast(
          summarizeWorkspaceAdGroupStateChange(result.successCount, result.failureCount, state),
          result.failureCount ? 'error' : 'success'
        );
        setAdGroupStatusModalOpen(false);
        discardDimensionSelection(WORKSPACE_FILTER_DIMENSIONS.adGroups);
        void queryClient.invalidateQueries({ queryKey: ['workspace-entity-page', 'ad-groups', profileId] });
        handleRefresh();
      } catch (error) {
        showToast(
          error instanceof Error ? error.message : AD_GROUP_STATUS_UPDATE_FALLBACK_MESSAGE,
          'error'
        );
      } finally {
        setAdGroupStatusApplying(false);
      }
    },
    [
      discardDimensionSelection,
      handleRefresh,
      profileId,
      queryClient,
      selectedAdGroups,
      showToast,
    ]
  );

  if (!profileId) {
    return <p className="p-6 text-body-sm text-foreground-muted">Missing account profile.</p>;
  }

  if (catalog.isLoading || !graph || !columnFiltered || !visible) {
    return <WorkspacePageSkeleton />;
  }

  if (catalog.isError) {
    return (
      <div className="mx-auto max-w-3xl p-6 font-sans">
        <div className="rounded-xl border border-error/20 bg-error-tint px-4 py-6 text-center">
          <p className="text-sm text-error-ink">
            {catalog.error instanceof Error ? catalog.error.message : 'Failed to load workspace'}
          </p>
          <button
            type="button"
            onClick={() => catalog.refetchAll()}
            className="mt-3 text-sm font-semibold text-brand hover:underline"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const adGroupsLoading = adGroupsPage.isPending;
  const keywordsLoading =
    keywordsPage.isPending || (compareEnabled && reportMetrics.previousLoading);

  const allAdGroupsSelected =
    adGroupsPage.rows.length > 0 &&
    adGroupsPage.rows.every((a) => normalizedPending.adGroupIds.includes(a.adGroupId));
  const allKeywordsSelected =
    keywordsPage.rows.length > 0 &&
    keywordsPage.rows.every((row) => normalizedPending.keywordIds.includes(row.keywordId));

  const adGroupCurrentTotals = totalsFromMetricRows(adGroupsPage.rows.map((r) => r.metrics));
  const adGroupPreviousTotals = totalsFromMetricRows(
    adGroupsPage.rows.map((r) => metricsForAdGroup(r.adGroupId, reportMetrics.previous))
  );
  const keywordCurrentTotals = totalsFromMetricRows(keywordsPage.rows.map((r) => r.metrics));
  const keywordPreviousTotals = totalsFromMetricRows(
    keywordsPage.rows.map((r) =>
      previousMetricsForKeywordRow(
        r,
        reportMetrics.previousKeywordLookup,
        reportMetrics.previousTargetRows
      )
    )
  );

  return (
    <div className="mx-auto max-w-[1600px] space-y-3.5 px-6 pb-[18px] pt-1 font-sans">
      <div className="flex flex-wrap items-start justify-between gap-6">
        <header>
          <h1 className="text-2xl font-semibold text-foreground">Workspace</h1>
          <p className="mt-0.5 text-body-md text-foreground-muted">
            Select rows in a table, then use that table&apos;s Apply filter button to filter the hierarchy.
          </p>
        </header>
        <div className="flex flex-wrap items-center gap-3 pb-1">
          <button
            type="button"
            onClick={() => setCreateCampaignPickerOpen(true)}
            className="text-sm px-3 py-1.5 bg-brand text-brand-foreground rounded-lg hover:bg-brand-hover font-medium inline-flex items-center gap-1.5"
          >
            <Plus className="w-4 h-4" />
            New campaign
          </button>
          <button
            type="button"
            onClick={() => reportSyncMutation.mutate()}
            disabled={!profileId || reportSyncMutation.isPending}
            title="Gap-fill last 12 complete calendar months of campaign, keyword, search-term, target, and placement reports. Months already in S3 are skipped; Amazon work is queued one month at a time with 429 backoff."
            className="inline-flex h-8 items-center gap-2 rounded-pill border border-border bg-surface px-3 text-body-sm text-foreground hover:border-brand disabled:opacity-60"
          >
            {reportSyncMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
            ) : (
              <Target className="h-4 w-4" aria-hidden />
            )}
            {reportSyncMutation.isPending ? 'Syncing…' : 'Sync'}
          </button>
          <button
            type="button"
            onClick={handleRefresh}
            className="inline-flex h-8 items-center gap-2 rounded-pill border border-border bg-surface px-3 text-body-sm text-foreground hover:border-brand"
          >
            <RefreshCw className="h-4 w-4" aria-hidden />
            Refresh
          </button>
        </div>
      </div>

      {metricsError ? (
        <div className="flex items-start gap-2 rounded-xl border border-warning/20 bg-warning-tint px-4 py-3 text-sm text-warning-ink">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
          <p>
            {metricsError.message}{' '}
            <button
              type="button"
              onClick={() => {
                reportMetrics.refetch();
                deferredMetrics.refetch();
              }}
              className="font-semibold underline"
            >
              Retry metrics
            </button>
          </p>
        </div>
      ) : null}

      {catalog.catalogIncomplete ? (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-warning/20 bg-warning-tint px-4 py-3 text-sm text-warning-ink">
          <div className="flex items-start gap-2">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
            <p>
              Keyword or ad group catalog may be incomplete. Cross-filter applies to loaded rows
              only. Loading the full catalog on large accounts can use significant browser memory.
            </p>
          </div>
          <button
            type="button"
            onClick={() => {
              const confirmed = window.confirm(
                'Load all keywords and ad groups for this account? On large accounts this may take several minutes and temporarily increase browser memory use.'
              );
              if (confirmed) catalog.loadFullCatalog();
            }}
            disabled={catalog.isFetchingFullCatalog}
            className="shrink-0 rounded-lg bg-warning px-3 py-1.5 text-sm font-medium text-white hover:bg-warning disabled:opacity-60"
          >
            {catalog.isFetchingFullCatalog ? 'Loading…' : 'Load full catalog'}
          </button>
        </div>
      ) : null}

      {visible.conflict ? (
        <div
          className="flex items-start gap-2 rounded-xl border border-error/20 bg-error-tint px-4 py-3 text-sm text-error-ink"
          role="alert"
        >
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
          <p>
            No entities match the current selection. Conflicting filters are active —{' '}
            <button type="button" onClick={clearAllFilters} className="font-semibold underline">
              clear all
            </button>{' '}
            or remove individual filters.
          </p>
        </div>
      ) : null}

      <WorkspaceFilterBar
        graph={graph}
        filter={appliedFilter}
        onRemoveChip={handleRemoveChip}
        onClearDimension={clearDimension}
        onClearAll={clearAllFilters}
        onRemoveStale={handleRemoveStale}
      />

      <WorkspaceKpiSummary
        current={kpiCurrentTotals}
        previous={kpiPreviousTotals}
        retail={retailKpis.current}
        previousRetail={retailKpis.previous}
        compareEnabled={compareEnabled}
        isLoading={adProductMetricsLoading}
        isFiltered={hasActiveCrossFilter}
        rangeSubtitle={rangeSubtitle}
        visibleKeys={visibleKpiKeys}
        headerActions={
          <WorkspaceCustomizeMetricsPopover
            selectedKeys={visibleKpiKeys}
            onApply={setVisibleKpiKeys}
          />
        }
      />

      <WorkspaceAdProductMetricsTable
        current={reportMetrics.current}
        previous={reportMetrics.previous}
        compareEnabled={compareEnabled}
        isLoading={adProductMetricsLoading}
        rangeSubtitle={rangeSubtitle}
        isFiltered={hasActiveCrossFilter}
        selection={makeDimensionSelection(WORKSPACE_FILTER_DIMENSIONS.adProducts)}
      />

      <WorkspaceTargetingTypeMetricsTable
        current={reportMetrics.currentByTargetingType}
        previous={reportMetrics.previousByTargetingType}
        currentTotals={reportMetrics.targetingTypeTotals.current}
        previousTotals={reportMetrics.targetingTypeTotals.previous}
        compareEnabled={compareEnabled}
        isLoading={coreLoading}
        rangeSubtitle={rangeSubtitle}
        isFiltered={hasActiveCrossFilter}
        selection={makeDimensionSelection(WORKSPACE_FILTER_DIMENSIONS.targetingTypes)}
      />

      <WorkspaceMatchTypeMetricsTable
        current={reportMetrics.currentByMatchType}
        previous={reportMetrics.previousByMatchType}
        currentTotals={reportMetrics.matchTypeTotals.current}
        previousTotals={reportMetrics.matchTypeTotals.previous}
        compareEnabled={compareEnabled}
        isLoading={coreLoading}
        rangeSubtitle={rangeSubtitle}
        isFiltered={hasActiveCrossFilter}
        selection={makeDimensionSelection(WORKSPACE_FILTER_DIMENSIONS.matchTypes)}
      />

      <LazyMount onVisible={enableDeferredSections}>
        <WorkspacePlacementMetricsTable
          current={deferredMetrics.currentByPlacement}
          previous={deferredMetrics.previousByPlacement}
          compareEnabled={compareEnabled}
          isLoading={deferredLoading}
          rangeSubtitle={rangeSubtitle}
          isFiltered={hasActiveCrossFilter}
          selection={makeDimensionSelection(WORKSPACE_FILTER_DIMENSIONS.placements)}
        />

        <WorkspaceAudienceMetricsTable
          current={deferredMetrics.currentByAudience}
          previous={deferredMetrics.previousByAudience}
          compareEnabled={compareEnabled}
          isLoading={deferredLoading}
          rangeSubtitle={rangeSubtitle}
          isFiltered={hasActiveCrossFilter}
        />
      </LazyMount>

      <WorkspacePortfoliosSection
        profileId={profileId}
        graph={graph}
        columnFilters={columnFilters}
        onColumnFiltersChange={updateColumnFilters}
        appliedFilter={normalizedApplied}
        pendingPortfolioIds={normalizedPending.portfolioIds}
        onChangePendingPortfolioIds={(update) =>
          setPendingFilter((prev) => ({ ...prev, portfolioIds: update(prev.portfolioIds) }))
        }
        selection={portfolioSelection}
        showApplyFilter
        onApplyFilter={() => applyDimensionFilter(WORKSPACE_FILTER_DIMENSIONS.portfolios)}
        onDiscardSelection={() => discardDimensionSelection(WORKSPACE_FILTER_DIMENSIONS.portfolios)}
        hasActiveCrossFilter={hasActiveCrossFilter}
        previousMetricsMaps={reportMetrics.previous}
      />

      <WorkspaceCampaignsSection
        profileId={profileId}
        graph={graph}
        columnFilters={columnFilters}
        onColumnFiltersChange={updateColumnFilters}
        appliedFilter={normalizedApplied}
        pendingCampaignKeys={normalizedPending.campaignKeys}
        onChangePendingCampaignKeys={(update) =>
          setPendingFilter((prev) => ({ ...prev, campaignKeys: update(prev.campaignKeys) }))
        }
        selection={campaignSelection}
        showApplyFilter
        onApplyFilter={() => applyDimensionFilter(WORKSPACE_FILTER_DIMENSIONS.campaigns)}
        onDiscardSelection={() => discardDimensionSelection(WORKSPACE_FILTER_DIMENSIONS.campaigns)}
        hasActiveCrossFilter={hasActiveCrossFilter}
        previousByCampaignKey={reportMetrics.previous.byCampaignKey}
        placementByCampaign={deferredMetrics.placementMetricsByCampaign}
        previousPlacementByCampaign={deferredMetrics.previousPlacementMetricsByCampaign}
        deferredLoading={deferredMetrics.deferredLoading}
        onCatalogRefresh={() => catalog.refetchAll()}
        onRefresh={handleRefresh}
        onOpenCampaignDetails={openCampaignDetails}
      />

      <WorkspaceSection
        title="Ad groups"
        countLabel={`${adGroupsPage.totalCount} total · ${rangeSubtitle}`}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <WorkspaceMetricFiltersPopover
              metrics={WORKSPACE_METRIC_COLUMNS}
              {...adGroupMetricFilters}
            />
            <WorkspaceExportButton
              onExport={() =>
                exportEntityTable({
                  entity: 'ad-groups',
                  filenamePrefix: 'workspace_ad_groups',
                  entityColumns: adGroupEntityColumns,
                  visibleIds: adGroupColumns.visibleIds,
                  columnFilters: {
                    search: columnFilters.adGroupSearch || undefined,
                    status:
                      columnFilters.adGroupState !== 'ALL'
                        ? columnFilters.adGroupState
                        : undefined,
                    adProduct:
                      columnFilters.adGroupAdProduct !== 'ALL'
                        ? columnFilters.adGroupAdProduct
                        : undefined,
                    campaignStatus:
                      columnFilters.adGroupCampaignState !== 'ALL'
                        ? columnFilters.adGroupCampaignState
                        : undefined,
                  },
                  sortBy: adGroupSort.sortBy,
                  sortDir: adGroupSort.sortDir,
                  metricFilters: adGroupMetricFilters.payload,
                })
              }
            />
            <ReportColumnPicker
              columns={workspaceColumnPickerOptions(adGroupEntityColumns)}
              selected={adGroupColumns.visibleIds}
              onChange={adGroupColumns.setVisibleIds}
              requiredColumnId="name"
              panelTitle="Visible columns"
            />
          </div>
        }
      >
        <ToolbarRow>
          <div className="flex flex-wrap items-end gap-3">
            <FilterSearch
              id="workspace-adgroup-search"
              label="Ad groups"
              value={columnFilters.adGroupSearch}
              onChange={(v) => updateColumnFilters({ adGroupSearch: v })}
            />
            <FilterSelect
              id="workspace-adgroup-product"
              label="Ad product"
              value={columnFilters.adGroupAdProduct}
              onChange={(v) =>
                updateColumnFilters({ adGroupAdProduct: v as typeof columnFilters.adGroupAdProduct })
              }
            >
              <option value="ALL">All</option>
              <option value="SP">SP</option>
              <option value="SB">SB</option>
              <option value="SD">SD</option>
            </FilterSelect>
            <FilterSelect
              id="workspace-adgroup-state"
              label="State"
              value={columnFilters.adGroupState}
              onChange={(v) =>
                updateColumnFilters({ adGroupState: v as typeof columnFilters.adGroupState })
              }
            >
              <option value="ALL">All</option>
              <option value="ENABLED">Enabled</option>
              <option value="PAUSED">Paused</option>
              <option value="ARCHIVED">Archived</option>
            </FilterSelect>
            <FilterSelect
              id="workspace-adgroup-campaign-state"
              label="Campaign state"
              value={columnFilters.adGroupCampaignState}
              onChange={(v) =>
                updateColumnFilters({
                  adGroupCampaignState: v as typeof columnFilters.adGroupCampaignState,
                })
              }
            >
              <option value="ALL">All</option>
              <option value="ENABLED">Enabled</option>
              <option value="PAUSED">Paused</option>
              <option value="ARCHIVED">Archived</option>
            </FilterSelect>
            <WorkspaceTableSelectionActions
              entityLabel="ad group"
              selectionCount={adGroupSelection.selectionCount}
              hasPendingChanges={adGroupSelection.hasPendingChanges}
              canApply={adGroupSelection.canApply}
              onApply={() => applyDimensionFilter(WORKSPACE_FILTER_DIMENSIONS.adGroups)}
              onClearSelection={() => discardDimensionSelection(WORKSPACE_FILTER_DIMENSIONS.adGroups)}
              disabled={adGroupsLoading}
              actions={adGroupLevelActionOptions()}
              onActionSelect={handleAdGroupActionSelect}
            />
          </div>
        </ToolbarRow>
        <WorkspaceTableShell
          isEmpty={!adGroupsPage.isPending && adGroupsPage.rows.length === 0}
          isLoading={adGroupsLoading}
          emptyMessage={
            hasActiveCrossFilter
              ? 'No ad groups match the current selection.'
              : 'No ad groups for this account.'
          }
          footer={
            <WorkspaceTablePagination
              page={adGroupPager.page}
              pageSize={adGroupPager.pageSize}
              total={adGroupsPage.totalCount}
              onPageChange={adGroupPager.setPage}
              onPageSizeChange={adGroupPager.setPageSize}
              disabled={adGroupsLoading}
            />
          }
        >
          <table ref={adGroupFreeze.tableRef} className={workspaceTableClass.table}>
            <thead>
              <tr>
                <WorkspaceCheckboxCell isHeader>
                  <SelectionCheckbox
                    checked={allAdGroupsSelected}
                    label="Select all visible ad groups"
                    disabled={adGroupsLoading}
                    onChange={(checked) => {
                      if (checked) {
                        const ids = new Set(pendingFilter.adGroupIds);
                        for (const row of adGroupsPage.rows) ids.add(row.adGroupId);
                        setPendingFilter((prev) => ({ ...prev, adGroupIds: [...ids] }));
                      } else {
                        const pageIds = new Set(adGroupsPage.rows.map((r) => r.adGroupId));
                        setPendingFilter((prev) => ({
                          ...prev,
                          adGroupIds: prev.adGroupIds.filter((id) => !pageIds.has(id)),
                        }));
                      }
                    }}
                  />
                </WorkspaceCheckboxCell>
                <WorkspaceOrderedHeaders
                  order={adGroupColumns.displayOrder}
                  entityColumns={adGroupEntityColumns}
                  sortBy={adGroupSort.sortBy}
                  sortDir={adGroupSort.sortDir}
                  onSort={adGroupSort.toggleSort}
                  onFreeze={adGroupColumns.freezeColumn}
                  frozenColumnIds={adGroupColumns.frozenColumnIds}
                  pinnedFrozenColumnId={adGroupColumns.pinnedFrozenColumnId}
                  frozenLeftById={adGroupFreeze.frozenLeftById}
                  onDragStartColumn={adGroupColumns.startDrag}
                  onDragHoverColumn={adGroupColumns.hoverReorder}
                  onDropColumn={adGroupColumns.commitDrop}
                  onDragEndColumn={adGroupColumns.endDrag}
                  activeDragColumnId={adGroupColumns.activeDragColumnId}
                  hoverColumnId={adGroupColumns.hoverColumnId}
                  hoverPosition={adGroupColumns.hoverPosition}
                  hoverInsertIndex={adGroupColumns.hoverInsertIndex}
                />
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {adGroupsPage.rows.map((row) => {
                const selected = pendingFilter.adGroupIds.includes(row.adGroupId);
                const previousMetrics = metricsForAdGroup(row.adGroupId, reportMetrics.previous);
                return (
                  <tr key={row.adGroupId} className={tableRowClass(selected)}>
                    <WorkspaceCheckboxCell>
                      <SelectionCheckbox
                        checked={selected}
                        label={`Select ad group ${row.name}`}
                        disabled={adGroupsLoading}
                        onChange={(checked) =>
                          setPendingFilter((prev) =>
                            toggleAdGroupFilter(prev, row.adGroupId, checked)
                          )
                        }
                    />
                  </WorkspaceCheckboxCell>
                  <WorkspaceOrderedMetricCells
                      order={adGroupColumns.displayOrder}
                      frozenColumnIds={adGroupColumns.frozenColumnIds}
                      frozenLeftById={adGroupFreeze.frozenLeftById}
                      metrics={row.metrics}
                      previousMetrics={previousMetrics}
                      compareEnabled={compareEnabled}
                      isLoading={adGroupsLoading}
                      activeDragColumnId={adGroupColumns.activeDragColumnId}
                      hoverColumnId={adGroupColumns.hoverColumnId}
                      hoverPosition={adGroupColumns.hoverPosition}
                      renderEntityCell={(columnId, meta) => {
                        if (columnId === 'name') {
                          return (
                            <td style={meta.style} className={`${workspaceTableClass.td} ${meta.className} font-medium text-foreground`}>
                              {row.name}
                            </td>
                          );
                        }
                        if (columnId === 'adProduct') {
                          return (
                            <WorkspaceMiddleCell style={meta.style} className={meta.className} alignToTitle>
                              <Badge variant="neutral">{row.adProduct}</Badge>
                            </WorkspaceMiddleCell>
                          );
                        }
                        if (columnId === 'campaign') {
                          return (
                            <td style={meta.style} className={`${workspaceTableClass.td} ${meta.className}`}>
                              {campaignNameById.get(row.campaignId) ?? row.campaignId}
                            </td>
                          );
                        }
                        if (columnId === 'state') {
                          return (
                            <WorkspaceMiddleCell style={meta.style} className={meta.className} alignToTitle>
                              {row.state}
                            </WorkspaceMiddleCell>
                          );
                        }
                        return null;
                      }}
                    />
                  </tr>
                );
              })}
            </tbody>
            {adGroupsPage.rows.length > 0 ? (
              <tfoot>
                <WorkspaceOrderedTotalsRow
                  order={adGroupColumns.displayOrder}
                  frozenColumnIds={adGroupColumns.frozenColumnIds}
                  frozenLeftById={adGroupFreeze.frozenLeftById}
                  current={adGroupCurrentTotals}
                  previous={adGroupPreviousTotals}
                  compareEnabled={compareEnabled}
                  isLoading={adGroupsLoading}
                />
              </tfoot>
            ) : null}
          </table>
        </WorkspaceTableShell>
      </WorkspaceSection>

      <WorkspaceSection
        title="Keywords"
        countLabel={`${keywordsPage.totalCount} total · ${rangeSubtitle}`}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <WorkspaceMetricFiltersPopover
              metrics={WORKSPACE_METRIC_COLUMNS}
              {...keywordMetricFilters}
            />
            <WorkspaceExportButton
              onExport={() =>
                exportEntityTable({
                  entity: 'keywords',
                  filenamePrefix: 'workspace_keywords',
                  entityColumns: KEYWORD_ENTITY_COLUMNS,
                  visibleIds: keywordColumns.visibleIds,
                  columnFilters: {
                    search: columnFilters.keywordSearch || undefined,
                    status:
                      columnFilters.keywordState !== 'ALL'
                        ? columnFilters.keywordState
                        : undefined,
                    matchType:
                      columnFilters.keywordMatchType !== 'ALL'
                        ? columnFilters.keywordMatchType
                        : undefined,
                  },
                  sortBy: keywordSort.sortBy,
                  sortDir: keywordSort.sortDir,
                  metricFilters: keywordMetricFilters.payload,
                })
              }
            />
            <ReportColumnPicker
              columns={workspaceColumnPickerOptions(KEYWORD_ENTITY_COLUMNS)}
              selected={keywordColumns.visibleIds}
              onChange={keywordColumns.setVisibleIds}
              requiredColumnId="keywordText"
              panelTitle="Visible columns"
            />
          </div>
        }
      >
        <ToolbarRow>
          <div className="flex flex-wrap items-end gap-3">
            <FilterSearch
              id="workspace-keyword-search"
              label="Keywords"
              value={columnFilters.keywordSearch}
              onChange={(v) => updateColumnFilters({ keywordSearch: v })}
            />
            <FilterSelect
              id="workspace-keyword-state"
              label="State"
              value={columnFilters.keywordState}
              onChange={(v) =>
                updateColumnFilters({ keywordState: v as typeof columnFilters.keywordState })
              }
            >
              <option value="ALL">All</option>
              <option value="ENABLED">Enabled</option>
              <option value="PAUSED">Paused</option>
              <option value="ARCHIVED">Archived</option>
            </FilterSelect>
            <FilterSelect
              id="workspace-keyword-match"
              label="Match type"
              value={columnFilters.keywordMatchType}
              onChange={(v) =>
                updateColumnFilters({
                  keywordMatchType: v as typeof columnFilters.keywordMatchType,
                })
              }
            >
              <option value="ALL">All</option>
              <option value="EXACT">Exact</option>
              <option value="PHRASE">Phrase</option>
              <option value="BROAD">Broad</option>
            </FilterSelect>
            <WorkspaceTableSelectionActions
              entityLabel="keyword"
              selectionCount={keywordActionSelectionCount}
              hasPendingChanges={keywordSelection.hasPendingChanges}
              canApply={keywordSelection.canApply && !keywordSelectAllMatching}
              onApply={() => applyDimensionFilter(WORKSPACE_FILTER_DIMENSIONS.keywords)}
              onClearSelection={() => {
                setKeywordSelectAllMatching(false);
                discardDimensionSelection(WORKSPACE_FILTER_DIMENSIONS.keywords);
              }}
              disabled={keywordsLoading}
              actions={keywordLevelActionOptions(isDemo)}
              onActionSelect={handleKeywordActionSelect}
            />
          </div>
        </ToolbarRow>
        {allKeywordsSelected &&
        !keywordSelectAllMatching &&
        keywordsPage.totalCount > keywordsPage.rows.length ? (
          <div className="mb-2 flex flex-wrap items-center gap-2 rounded-lg border border-border bg-surface-muted px-3 py-2 text-sm text-foreground">
            <span>
              All {keywordsPage.rows.length} keywords on this page are selected.
            </span>
            <button
              type="button"
              className="font-medium text-foreground underline underline-offset-2"
              onClick={() => setKeywordSelectAllMatching(true)}
            >
              Select all {keywordsPage.totalCount} matching keywords
            </button>
          </div>
        ) : null}
        {keywordSelectAllMatching ? (
          <div className="mb-2 flex flex-wrap items-center gap-2 rounded-lg border border-border bg-surface-muted px-3 py-2 text-sm text-foreground">
            <span>All {keywordsPage.totalCount} matching keywords are selected.</span>
            <button
              type="button"
              className="font-medium text-foreground underline underline-offset-2"
              onClick={() => {
                setKeywordSelectAllMatching(false);
                discardDimensionSelection(WORKSPACE_FILTER_DIMENSIONS.keywords);
              }}
            >
              Clear selection
            </button>
          </div>
        ) : null}
        <WorkspaceTableShell
          isEmpty={!keywordsPage.isPending && keywordsPage.rows.length === 0}
          isLoading={keywordsLoading}
          emptyMessage={
            hasActiveCrossFilter
              ? 'No keywords match the current selection.'
              : 'No keywords for this account.'
          }
          footer={
            <WorkspaceTablePagination
              page={keywordPager.page}
              pageSize={keywordPager.pageSize}
              total={keywordsPage.totalCount}
              onPageChange={keywordPager.setPage}
              onPageSizeChange={keywordPager.setPageSize}
              disabled={keywordsLoading}
            />
          }
        >
          <table ref={keywordFreeze.tableRef} className={workspaceTableClass.table}>
            <thead>
              <tr>
                <WorkspaceCheckboxCell isHeader>
                  <SelectionCheckbox
                    checked={keywordSelectAllMatching || allKeywordsSelected}
                    label="Select all visible keywords"
                    disabled={keywordsLoading}
                    onChange={(checked) => {
                      setKeywordSelectAllMatching(false);
                      const ids = keywordsPage.rows.map((row) => row.keywordId);
                      setPendingFilter((prev) => toggleKeywordFilter(prev, ids, checked));
                    }}
                  />
                </WorkspaceCheckboxCell>
                <WorkspaceOrderedHeaders
                  order={keywordColumns.displayOrder}
                  entityColumns={KEYWORD_ENTITY_COLUMNS}
                  sortBy={keywordSort.sortBy}
                  sortDir={keywordSort.sortDir}
                  onSort={keywordSort.toggleSort}
                  onFreeze={keywordColumns.freezeColumn}
                  frozenColumnIds={keywordColumns.frozenColumnIds}
                  pinnedFrozenColumnId={keywordColumns.pinnedFrozenColumnId}
                  frozenLeftById={keywordFreeze.frozenLeftById}
                  onDragStartColumn={keywordColumns.startDrag}
                  onDragHoverColumn={keywordColumns.hoverReorder}
                  onDropColumn={keywordColumns.commitDrop}
                  onDragEndColumn={keywordColumns.endDrag}
                  activeDragColumnId={keywordColumns.activeDragColumnId}
                  hoverColumnId={keywordColumns.hoverColumnId}
                  hoverPosition={keywordColumns.hoverPosition}
                  hoverInsertIndex={keywordColumns.hoverInsertIndex}
                />
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {keywordsPage.rows.map((row) => {
                const selected =
                  keywordSelectAllMatching ||
                  normalizedPending.keywordIds.includes(row.keywordId);
                const previousMetrics = previousMetricsForKeywordRow(
                  row,
                  reportMetrics.previousKeywordLookup,
                  reportMetrics.previousTargetRows
                );
                return (
                  <tr key={row.keywordId} className={tableRowClass(selected)}>
                    <WorkspaceCheckboxCell>
                      <SelectionCheckbox
                        checked={selected}
                        label={`Select keyword ${row.keywordText}`}
                        disabled={keywordsLoading}
                        onChange={(checked) => {
                          setKeywordSelectAllMatching(false);
                          setPendingFilter((prev) =>
                            toggleKeywordFilter(prev, [row.keywordId], checked)
                          );
                        }}
                    />
                  </WorkspaceCheckboxCell>
                  <WorkspaceOrderedMetricCells
                      order={keywordColumns.displayOrder}
                      frozenColumnIds={keywordColumns.frozenColumnIds}
                      frozenLeftById={keywordFreeze.frozenLeftById}
                      metrics={row.metrics}
                      previousMetrics={previousMetrics}
                      compareEnabled={compareEnabled}
                      isLoading={keywordsLoading}
                      activeDragColumnId={keywordColumns.activeDragColumnId}
                      hoverColumnId={keywordColumns.hoverColumnId}
                      hoverPosition={keywordColumns.hoverPosition}
                      renderEntityCell={(columnId, meta) => {
                        if (columnId === 'keywordText') {
                          return (
                            <td style={meta.style} className={`${workspaceTableClass.td} ${meta.className} font-medium text-foreground`}>
                              {row.keywordText}
                            </td>
                          );
                        }
                        if (columnId === 'adProduct') {
                          return (
                            <WorkspaceMiddleCell style={meta.style} className={meta.className} alignToTitle>
                              <Badge variant="neutral">{row.adProduct}</Badge>
                            </WorkspaceMiddleCell>
                          );
                        }
                        if (columnId === 'matchType') {
                          return (
                            <WorkspaceMiddleCell style={meta.style} className={meta.className} alignToTitle>
                              <span
                                className={
                                  row.rowKind === 'auto_target'
                                    ? 'text-xs font-medium'
                                    : 'text-xs font-medium uppercase tracking-wider'
                                }
                              >
                                {row.rowKind === 'auto_target'
                                  ? workspaceKeywordMatchLabel(row.matchType)
                                  : row.matchType}
                              </span>
                            </WorkspaceMiddleCell>
                          );
                        }
                        if (columnId === 'bid') {
                          const editableTarget =
                            row.rowKind === 'auto_target'
                              ? null
                              : resolveEditableWorkspaceKeywordBid(row);
                          return (
                            <td
                              style={meta.style} className={`${workspaceTableClass.td} ${meta.className} text-center font-mono tabular-nums text-[11px]`}
                            >
                              <EditableKeywordBidCell
                                profileId={profileId}
                                displayValue={formatCurrencyValue(
                                  typeof row.bid === 'number' ? row.bid : null
                                )}
                                editableTarget={editableTarget}
                                suggestion={
                                  editableTarget
                                    ? keywordSuggestedBids.suggestedBidsByKeywordId?.get(row.keywordId)
                                    : null
                                }
                                keywordLabel={row.keywordText}
                                onUpdated={handleKeywordBidUpdated}
                              />
                            </td>
                          );
                        }
                        if (columnId === 'campaign') {
                          const campaignLabel =
                            row.rowKind === 'auto_target' && !row.campaignId
                              ? 'All campaigns'
                              : (campaignNameById.get(row.campaignId) ?? row.campaignId);
                          return (
                            <td style={meta.style} className={`${workspaceTableClass.td} ${meta.className} text-foreground-muted`}>
                              {campaignLabel}
                            </td>
                          );
                        }
                        if (columnId === 'state') {
                          return (
                            <WorkspaceMiddleCell style={meta.style} className={meta.className} alignToTitle>
                              {row.state}
                            </WorkspaceMiddleCell>
                          );
                        }
                        return null;
                      }}
                    />
                  </tr>
                );
              })}
            </tbody>
            {keywordsPage.rows.length > 0 ? (
              <tfoot>
                <WorkspaceOrderedTotalsRow
                  order={keywordColumns.displayOrder}
                  frozenColumnIds={keywordColumns.frozenColumnIds}
                  frozenLeftById={keywordFreeze.frozenLeftById}
                  current={keywordCurrentTotals}
                  previous={keywordPreviousTotals}
                  compareEnabled={compareEnabled}
                  isLoading={keywordsLoading}
                />
              </tfoot>
            ) : null}
          </table>
        </WorkspaceTableShell>
      </WorkspaceSection>

      <LazyMount onVisible={enableDeferredSections}>
        <WorkspaceSearchTermsTable
          profileId={profileId}
          startDate={rangeState.current.startDate}
          endDate={rangeState.current.endDate}
          previousStartDate={rangeState.previous.startDate}
          previousEndDate={rangeState.previous.endDate}
          compareEnabled={compareEnabled}
          graph={graph}
          appliedFilter={normalizedApplied}
          enabled={reportMetrics.coreSettled && deferredSectionsVisible}
          rangeSubtitle={rangeSubtitle}
          isFiltered={hasActiveCrossFilter}
          selection={makeDimensionSelection(WORKSPACE_FILTER_DIMENSIONS.searchTermKeys)}
          isDemo={isDemo}
          demoCampaigns={demoCampaignOptions}
          demoAdGroups={demoAdGroupOptions}
        />
      </LazyMount>

      <LazyMount onVisible={enableProductAdsSections}>
        <WorkspaceAdvertisedProductTable
          profileId={profileId}
          appliedFilter={normalizedApplied}
          enabled={reportMetrics.coreSettled && productAdsVisible}
          rangeSubtitle={rangeSubtitle}
          isFiltered={hasActiveCrossFilter}
          selection={makeDimensionSelection(WORKSPACE_FILTER_DIMENSIONS.asins)}
        />

        <WorkspaceAdProductsTable
          profileId={profileId}
          appliedFilter={normalizedApplied}
          enabled={reportMetrics.coreSettled && productAdsVisible}
          rangeSubtitle={rangeSubtitle}
          isFiltered={hasActiveCrossFilter}
          selection={makeDimensionSelection(WORKSPACE_FILTER_DIMENSIONS.adProductRowKeys)}
        />
      </LazyMount>

      {adGroupStatusModalOpen ? (
        <WorkspaceStateChangeModal
          title="Change Ad Group Status"
          entityLabel="ad group"
          selectionCount={selectedAdGroups.length}
          pending={adGroupStatusApplying}
          onCancel={() => setAdGroupStatusModalOpen(false)}
          onConfirm={(state) => void handleConfirmAdGroupState(state)}
        />
      ) : null}

      {keywordBidModalOpen ? (
        <WorkspaceKeywordBidModal
          selectionCount={keywordActionSelectionCount}
          pending={keywordBidJob.isRunning}
          onCancel={() => setKeywordBidModalOpen(false)}
          onConfirm={(mode, value) => void handleConfirmKeywordBid(mode, value)}
        />
      ) : null}

      {keywordSuggestedBidModalOpen ? (
        <WorkspaceSuggestedBidModal
          selectionCount={keywordActionSelectionCount}
          pending={keywordBidJob.isRunning}
          onCancel={() => setKeywordSuggestedBidModalOpen(false)}
          onConfirm={() => void handleConfirmSuggestedBid()}
        />
      ) : null}

      {keywordBidJob.job ? (
        <WorkspaceKeywordBidProgressModal
          job={keywordBidJob.job}
          failedResults={keywordBidJob.failedResults}
          isRunning={keywordBidJob.isRunning}
          onClose={() => keywordBidJob.reset()}
          onRetryFailed={() => {
            if (!keywordBidJob.job) return;
            void keywordBidJob.retryFailed(keywordBidJob.job.id).then((final) => {
              const failed = final.totals.failed ?? 0;
              const applied = final.totals.applied ?? 0;
              if (failed > 0) {
                showToast(
                  `Retry finished: ${applied} applied, ${failed} still failed`,
                  'error'
                );
              } else {
                showToast(`Retry applied ${applied} keyword${applied === 1 ? '' : 's'}`, 'success');
              }
            }).catch((error) => {
              showToast(
                error instanceof Error ? error.message : 'Failed to retry bid changes',
                'error'
              );
            });
          }}
        />
      ) : null}

      <CampaignTypePickerModal
        profileId={profileId}
        isOpen={createCampaignPickerOpen}
        onClose={() => setCreateCampaignPickerOpen(false)}
      />

      {demoModals}
    </div>
  );
}
